import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const REASONS = [
  { id: "unkind", label: "Unkind or degrading" },
  { id: "privacy", label: "Shares private info" },
  { id: "spam", label: "Spam or advertising" },
  { id: "misinformation", label: "Misinformation" },
  { id: "political", label: "Political content" },
  { id: "off_topic", label: "Not a cost-saving tip" },
  { id: "other", label: "Something else" },
];

export default function ReportDialog({ tipId, tipText, open, onOpenChange }) {
  const [reason, setReason] = useState(REASONS[0].id);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/tips/${tipId}/report`, { reason, details });
      if (data.already_reported) {
        toast.info("You've already reported this tip — our team is on it.");
      } else {
        toast.success("Thank you. Our team will review this soon.");
      }
      onOpenChange(false);
      setDetails("");
      setReason(REASONS[0].id);
    } catch {
      toast.error("Could not submit report");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="report-dialog">
        <DialogHeader>
          <div className="w-11 h-11 rounded-2xl bg-[#E76F51]/12 flex items-center justify-center mb-2">
            <Flag className="w-5 h-5 text-[#E76F51]" />
          </div>
          <DialogTitle className="font-heading text-2xl text-[#1B2A4A]">Report this tip</DialogTitle>
          <DialogDescription className="text-[#1B2A4A]/70">
            Help us keep Surviving Together warm and useful. Reports are anonymous to other members.
          </DialogDescription>
        </DialogHeader>

        {tipText && (
          <div className="rounded-2xl bg-[#F7F5F2] px-4 py-3 border border-[#1B2A4A]/8">
            <p className="text-sm text-[#1B2A4A]/80 line-clamp-3">"{tipText}"</p>
          </div>
        )}

        <div className="space-y-3">
          <p className="text-sm font-semibold text-[#1B2A4A]">What's wrong with it?</p>
          <RadioGroup value={reason} onValueChange={setReason} className="space-y-1.5" data-testid="report-reasons">
            {REASONS.map((r) => (
              <div key={r.id} className="flex items-center gap-3">
                <RadioGroupItem value={r.id} id={`reason-${r.id}`} data-testid={`report-reason-${r.id}`} />
                <Label htmlFor={`reason-${r.id}`} className="text-sm text-[#1B2A4A]/85 font-medium cursor-pointer">
                  {r.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-semibold text-[#1B2A4A]">Anything else? (optional)</Label>
          <Textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            data-testid="report-details"
            rows={3}
            maxLength={300}
            placeholder="A few extra words help our team decide faster."
            className="rounded-2xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50 resize-none"
          />
        </div>

        <DialogFooter className="flex-row justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="report-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="report-submit"
            className="rounded-full bg-[#E76F51] hover:bg-[#E76F51]/90 text-white"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
